import type { ColoredMaterial } from "@color-finder/color-finder-api/models";
import { SearchBoxLocal } from "@covestro/mui-theme";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { DemoBanner } from "components/banner/DemoBanner";
import { ContactRequestButton } from "components/commons/ContactRequestButton";
import type { ColorFilterValue } from "components/filter/color-filter/ColorFilter";
import { oemColorTarget } from "components/filter/color-filter/oem-color";
import { SearchFilter } from "components/filter/SearchFilter";
import { FilterChips } from "components/filter-chips/FilterChips";
import { getVisibleCategoryNavItems } from "components/navigation/categoryNavItems";
import { SearchResultsGrid } from "components/search-results/grid/SearchResultsGrid";
import { ZoomSlider } from "components/search-results/grid/ZoomSlider";
import {
  type SearchResultsLayout,
  SearchResultsLayoutInput,
} from "components/search-results/SearchResultsLayoutInput";
import { SearchResultsTable } from "components/search-results/table/SearchResultsTable";
import { PAGE_SIZE_OPTIONS } from "constants/pagination";
import Fuse from "fuse.js";
import { useAuthUser } from "hooks/authentication";
import { useColoredMaterials } from "hooks/colored-materials";
import {
  type FilterEntry,
  useFilters,
  useSingleFilter,
  useTitleForFilters,
} from "hooks/filters";
import {
  resolveDefaultSort,
  type SortEntry,
  sortColoredMaterials,
} from "hooks/sort";
import { useTitle } from "hooks/title";
import { useIsDesktop } from "hooks/use-breakpoint";
import { useFeatureFlags } from "hooks/use-config";
import { useEffectEvent } from "hooks/utils/effect-event";
import type { CdcUserAttributes } from "models/cdcUser";
import { defaultFilters } from "pages/start/StartPage";
import {
  DiscoveryNavOutlet,
  useRegisterDiscoveryNav,
} from "providers/DiscoveryNavProvider";
import { useUserInfoContext } from "providers/UserInfoProvider";
import type React from "react";
import {
  type ChangeEvent,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { authenNavigate } from "service/utils/authen-navigate";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { SearchSort } from "./SearchSort";
import { SearchUploadMatch } from "./SearchUploadMatch";

const Heading = ({
  children,
  marginBottom = 1,
}: PropsWithChildren<{ marginBottom?: number }>) => (
  <Typography
    component="span"
    fontWeight={500}
    marginRight={1}
    marginBottom={marginBottom}
    fontSize="var(--title-t2-18-regular-size, 18px)"
  >
    {children}
  </Typography>
);

/**
 * Registers SearchPage's discovery nav: no active tab (its filters don't map
 * 1:1 back to a single tab), and a tab click returns to the tab's own entry
 * UI on StartPage rather than trying to reproduce its results inline here
 * (CX-3737 Phase 4 decision).
 */
function useSearchPageDiscoveryNav() {
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const discoveryNavItems = useMemo(
    () => getVisibleCategoryNavItems(flags.uploadMatch, flags.discoveryBrowse),
    [flags.uploadMatch, flags.discoveryBrowse],
  );
  const handleDiscoveryNavSelect = useCallback(
    (id: string | null) => navigate("/", { state: { requestedTab: id } }),
    [navigate],
  );
  useRegisterDiscoveryNav({
    items: discoveryNavItems,
    activeId: null,
    onSelect: handleDiscoveryNavSelect,
  });
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing top-level page component; the CX-3737 change adds a single useSearchPageDiscoveryNav() call. A full decomposition is out of scope for this fix.
export const SearchPage = () => {
  authenNavigate();
  useSearchPageDiscoveryNav();

  const { state } = useLocation();
  const [urlFilters, setUrlFilters, urlSort, setUrlSort] = useFilters();
  const [filters, setFilters] = useState<Array<FilterEntry>>(
    state ? state.filters : urlFilters,
  );
  const [sort, setSort] = useState<Array<SortEntry>>(
    urlSort?.length ? urlSort : [],
  );
  const [localSort, setLocalSort] = useState<SortEntry | undefined>();
  const [layout, setLayout] = useState<SearchResultsLayout>("table");
  const [query, setQuery] = useState("");

  const sortOrDefault = sort.length ? sort : resolveDefaultSort(filters);
  const { t } = useTranslation();

  const cdcUser: CdcUserAttributes | undefined =
    getDataFromLocalstorageByKey("cdcUser");
  const { user } = useAuthUser();
  const isLogin = Boolean(user?.email) || Boolean(cdcUser);

  const titlesForFilters = useTitleForFilters(filters);
  if (titlesForFilters.length === 0) {
    titlesForFilters.push("Search");
  }
  useTitle([...titlesForFilters, "Imagio Color Finder"].join(" · "));
  const { userInfo } = useUserInfoContext();
  const isDemo = userInfo?.accessLevel === "DEMO";
  const setAndSaveFilters = (filters: Array<FilterEntry>) => {
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing color-system filter logic, unchanged and unrelated to the CX-3737 discovery-nav fix. Decomposing it is its own ticket.
    setFilters((prevFilters) => {
      const filterColor = filters.find((i) => i.type === "color");
      const isColorSystemIndex = filters.findIndex(
        (i) => i.type === "isColorSystem",
      );
      const colorSystemCodeIndex = filters.findIndex(
        (i) => i.type === "colorSystemCode",
      );

      // Auto-tick the checkbox for supported color systems (only when not explicitly unchecked by user)
      const supportedColorSystems = ["ncs", "ral_classic", "ral_design_plus"];
      const selectedColorSystem = filterColor?.value?.colorSystem;
      const prevColorSystem = prevFilters.find((i) => i.type === "color")?.value
        ?.colorSystem;
      const prevIsColorSystem = prevFilters.find(
        (i) => i.type === "isColorSystem",
      );

      // Only auto-tick when:
      // 1. Switching to a supported color system for the first time (no previous isColorSystem filter)
      // 2. Switching between different supported color systems (update the value)
      if (
        selectedColorSystem &&
        supportedColorSystems.includes(selectedColorSystem)
      ) {
        // Check if this is a color system change (not just a re-render)
        const isColorSystemChange = prevColorSystem !== selectedColorSystem;

        if (isColorSystemIndex === -1 && isColorSystemChange) {
          // Auto-add only when switching to a supported system for the first time
          filters.push({
            type: "isColorSystem",
            value: selectedColorSystem,
          });
        } else if (
          isColorSystemIndex !== -1 &&
          isColorSystemChange &&
          prevIsColorSystem
        ) {
          // Update when switching between supported systems, but only if it was previously set
          filters[isColorSystemIndex].value = selectedColorSystem;
        }
        // If isColorSystemIndex === -1 and it's not a change, user has unchecked it - don't re-add
      } else if (
        selectedColorSystem &&
        !supportedColorSystems.includes(selectedColorSystem)
      ) {
        // Remove isColorSystem filter when switching to an unsupported color system
        if (isColorSystemIndex !== -1) {
          filters.splice(isColorSystemIndex, 1);
        }
      }

      const isColorSystem = filters.find((i) => i.type === "isColorSystem");
      let codeValue: string | undefined;
      if (isColorSystem?.value === "ncs" && filterColor?.value.color?.name) {
        codeValue = filterColor.value.color.name;
      } else if (filterColor?.value.color?.code) {
        codeValue = filterColor.value.color.code;
      }

      if (codeValue) {
        if (colorSystemCodeIndex !== -1) {
          filters[colorSystemCodeIndex].value = codeValue;
        } else {
          filters.push({
            type: "colorSystemCode",
            value: codeValue,
          });
        }
      } else {
        filters = filters.filter((i) => i.type !== "colorSystemCode");
      }
      setUrlFilters(filters);
      return filters;
    });
    setPage(1);
    setLocalPageSearch(1);
  };

  const setAndSaveSort = (sort: Array<SortEntry>) => {
    setUrlSort(sort);
    setSort(sort);
    setLocalSort(undefined);
  };

  const paginate = (
    data: Array<any>,
    page: number = 1,
    itemPerPage: number = 50,
  ) => {
    const start = (page - 1) * itemPerPage;
    return data.slice(start, start + itemPerPage);
  };

  const [colorFilter] = useSingleFilter<ColorFilterValue>(
    filters,
    setAndSaveFilters,
    "color",
  );

  const setDefaultFilterParams = useEffectEvent(() => {
    if (state !== null) {
      setUrlFilters(state.filters);
    }
  });
  useEffect(setDefaultFilterParams, [setDefaultFilterParams]);

  // const pageSize = 50;
  const [page, setPage] = useState(1);
  const [localPageSearch, setLocalPageSearch] = useState(1);
  const [goToPage, setGoToPage] = useState<string>("");
  const [itemPerPage, setItemPerPage] = useState(50);
  const [localItemPerPage, setLocalItemPerPage] = useState(50);
  const [isLocalSearch, setIsLocalSearch] = useState<boolean>(false);

  const [coloredMaterials, error, isLoading] = useColoredMaterials(
    filters,
    sortOrDefault,
    page - 1,
    itemPerPage,
  );
  const [dataSearch, setDataSearch] = useState<Array<any>>(
    coloredMaterials?.results ?? [],
  );
  const [pageData, setPageData] = useState<Array<any>>(
    paginate(dataSearch, localPageSearch, localItemPerPage) ?? [],
  );

  // Keeps the order the backend returned so clearing the local sort can restore it.
  const serverOrderResults = useRef<Array<ColoredMaterial>>(
    coloredMaterials?.results ?? [],
  );

  const replaceLocalResults = (results: Array<ColoredMaterial>) => {
    serverOrderResults.current = results;
    setDataSearch(results);
    setLocalSort(undefined);
  };

  const sortLocalResults = (entry?: SortEntry) => {
    const sorted = entry
      ? sortColoredMaterials({
          values: serverOrderResults.current,
          property: entry.property,
          direction: entry.direction,
        })
      : serverOrderResults.current;
    setLocalSort(entry);
    setDataSearch(sorted);
    setLocalPageSearch(1);
    setPageData(paginate(sorted, 1, localItemPerPage));
  };

  const useTwoColumnLayout = useIsDesktop();
  const resetFilters = () => {
    setAndSaveFilters(defaultFilters);
  };

  const filterChips = (
    <FilterChips
      isSearchPage={true}
      filters={filters}
      onChange={setAndSaveFilters}
      onFilterReset={resetFilters}
      t={t}
    />
  );

  const pageCount = Math.ceil(
    (coloredMaterials?.totalCount ?? 0) / itemPerPage,
  );
  const changePage = (_: unknown, p: number) => {
    if (isLocalSearch) {
      setLocalPageSearch(p);
      setPageData(paginate(dataSearch, p, localItemPerPage));
    } else {
      setPage(p);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const [gridSize, setGridSize] = useState(2);
  const [selectedMaterial, setSelectedMaterial] = useState<
    ColoredMaterial | undefined
  >();

  const handlePagesizeChange = (e: SelectChangeEvent) => {
    const newItemPerPage = Number(e.target.value);

    if (isLocalSearch) {
      setLocalItemPerPage(Number(e.target.value));
      const localPageCount = Math.ceil(
        (dataSearch.length ?? 0) / newItemPerPage,
      );
      const newLocalPageSearch =
        localPageSearch > localPageCount ? 1 : localPageSearch;

      setLocalPageSearch(newLocalPageSearch);
      setPageData(
        paginate(dataSearch, newLocalPageSearch, Number(e.target.value)),
      );
    } else {
      setItemPerPage(newItemPerPage);
    }
  };

  const handlePageChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (
      event.target.value !== "" &&
      event.target.value &&
      Number(event.target.value) <= pageCount
    ) {
      setGoToPage(event.target.value);
    } else {
      setGoToPage("");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "Enter" &&
      goToPage !== "" &&
      Number(goToPage) &&
      Number(goToPage) <= pageCount
    ) {
      changePage(null, Number(goToPage));
    } else {
      setGoToPage("");
    }
  };

  const handleBlur = () => {
    if (goToPage !== "" && Number(goToPage) && Number(goToPage) <= pageCount) {
      changePage(null, Number(goToPage));
    } else {
      setGoToPage("");
    }
  };

  const onSearch = (searchQuery: string) => {
    if (searchQuery.length === 0) {
      replaceLocalResults(coloredMaterials?.results ?? []);
      setPageData(paginate(coloredMaterials?.results ?? []));
      return;
    }

    if (coloredMaterials?.results) {
      const trimmed = searchQuery.trim();
      const isCodeSearch = /^[\d\w-]+$/.test(trimmed) && /\d/.test(trimmed);

      let searchResults: Array<ColoredMaterial>;
      if (isCodeSearch) {
        const q = trimmed.toLowerCase();
        searchResults = coloredMaterials.results.filter(
          (item) =>
            item.color.finalColorCode?.toLowerCase().includes(q) ||
            item.material.publicGradeName?.toLowerCase().includes(q),
        );
      } else {
        const fuse = new Fuse(coloredMaterials.results, {
          keys: [
            "material.publicGradeName",
            "color.finalColorCode",
            "availability.colorStatus",
          ],
          includeScore: true,
          threshold: 0.3,
        });
        searchResults = fuse.search(trimmed).map((result) => result.item);
      }

      replaceLocalResults(searchResults);
      setLocalPageSearch(1);
      setPageData(paginate(searchResults, 1, localItemPerPage));
    } else {
      replaceLocalResults([]);
      setPageData([]);
    }
  };

  useEffect(() => {
    if (
      coloredMaterials &&
      coloredMaterials.totalCount > 0 &&
      coloredMaterials.totalCount <= 500 &&
      !isLoading
    ) {
      serverOrderResults.current = coloredMaterials.results;
      setDataSearch(coloredMaterials.results);
      setLocalSort(undefined);
      setLocalPageSearch(1);
      setPageData(paginate(coloredMaterials.results));
      setIsLocalSearch(true);
    } else {
      setIsLocalSearch(false);
      serverOrderResults.current = [];
      setDataSearch([]);
      setLocalSort(undefined);
    }
  }, [coloredMaterials, isLoading]);

  return (
    <>
      <DiscoveryNavOutlet />
      <Grid
        container
        mt={0}
        mb={4}
        rowSpacing={3}
        columnSpacing={4}
        sx={{
          paddingLeft: { xs: "16px", sm: "24px" },
          paddingRight: { xs: "16px", sm: "24px" },
        }}
      >
        {useTwoColumnLayout && (
          <Grid size={12}>
            <Toolbar variant="dense" disableGutters>
              <Heading>{t("src_active_filter")}</Heading>
            </Toolbar>
            {filterChips}
          </Grid>
        )}
        <Grid size={{ xs: 12, lg: 4, xxl: 3 }}>
          <Toolbar disableGutters>
            <Heading>{t("src_search_criteria")}</Heading>
            <Button
              sx={{
                height: 16,
                fontSize: "var(--body-b2-14-regular-size, 14px)",
                position: "absolute",
                right: { xs: 16, md: 0 },
              }}
              onClick={resetFilters}
              size="medium"
            >
              <ReplayIcon
                fontSize="small"
                sx={{ marginRight: "2px", marginBottom: "2px" }}
              />
              {t("src_reset_all")}
            </Button>
          </Toolbar>
          <SearchUploadMatch />
          <SearchFilter
            childColumns={useTwoColumnLayout ? 3 : 12}
            value={filters}
            defaultExpanded={useTwoColumnLayout}
            onChange={setAndSaveFilters}
          ></SearchFilter>
        </Grid>
        <Grid size={{ xs: 12, lg: 8, xxl: 9 }}>
          <Toolbar
            disableGutters
            sx={{
              flexWrap: { xs: "wrap", md: "nowrap" },
              rowGap: 1,
              columnGap: { xs: 1, md: 0 },
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                flexBasis: { xs: "100%", md: "auto" },
              }}
            >
              <Heading marginBottom={0}>{t("src_results")}</Heading>
              {coloredMaterials && coloredMaterials.totalCount > 0 && (
                <Typography
                  component="span"
                  color="text.secondary"
                  fontSize="var(--body-b3-12-regular-size, 12px)"
                >
                  {isLocalSearch
                    ? dataSearch.length > 0
                      ? `${(localPageSearch - 1) * localItemPerPage + 1} to ${Math.min(localPageSearch * localItemPerPage, dataSearch.length)} of ${dataSearch.length}`
                      : `0 of 0`
                    : `${(page - 1) * itemPerPage + 1} to ${Math.min(page * itemPerPage, coloredMaterials.totalCount)} of ${coloredMaterials.totalCount}`}
                </Typography>
              )}
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                mr: { xs: 0, md: 4 },
                display: "flex",
                alignItems: "center",
                flexBasis: { xs: "100%", md: "auto" },
              }}
            >
              <SearchBoxLocal
                onSearch={onSearch}
                placeHolderText={
                  (coloredMaterials?.totalCount &&
                    coloredMaterials?.totalCount > 500) ||
                  isLoading
                    ? `${t("src_search_within_result_out")}`
                    : `${t("src_search_within_result_in")}`
                }
                width={"100%"}
                total={coloredMaterials?.totalCount}
                loading={isLoading}
                query={query}
                setQuery={setQuery}
              />
            </Box>
            <Box
              sx={{
                flexGrow: 0,
                mr: { xs: 1, md: 4 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <SearchSort
                query={query}
                value={sort}
                onChange={setAndSaveSort}
              />
            </Box>
            {layout === "grid" && (
              <Box
                sx={{
                  flexGrow: 0,
                  mr: { xs: 1, md: 4 },
                  display: { xs: "none", sm: "flex" },
                  alignItems: "center",
                }}
              >
                <ZoomSlider
                  value={gridSize}
                  onChange={setGridSize}
                  min={1}
                  max={3}
                />
              </Box>
            )}

            <SearchResultsLayoutInput value={layout} onChange={setLayout} />
          </Toolbar>
          <DemoBanner isDemo={isDemo} />
          <Stack rowGap={2} alignItems={"center"} sx={{ width: "100%" }}>
            {!useTwoColumnLayout && filterChips}
            {!error && layout === "grid" && (
              <SearchResultsGrid
                onFilterReset={resetFilters}
                target={
                  colorFilter?.color ?? oemColorTarget(colorFilter?.oemColor)
                }
                values={isLocalSearch ? pageData : coloredMaterials?.results}
                gridSize={gridSize}
                loading={isLoading}
                onMaterialSelect={setSelectedMaterial}
              />
            )}
            {!error && layout === "table" && (
              <Paper sx={{ width: "100%" }}>
                <SearchResultsTable
                  onFilterReset={resetFilters}
                  target={
                    colorFilter?.color ?? oemColorTarget(colorFilter?.oemColor)
                  }
                  values={!isLocalSearch ? coloredMaterials?.results : pageData}
                  sort={sortOrDefault}
                  onSortChange={setAndSaveSort}
                  loading={isLoading}
                  isLocalSearch={isLocalSearch}
                  localSort={localSort}
                  onLocalSort={sortLocalResults}
                  onMaterialSelect={setSelectedMaterial}
                  filters={filters}
                />
              </Paper>
            )}
            {error && (
              <Paper sx={{ width: "100%" }}>
                <Alert severity="error">
                  <AlertTitle>{t("src_failed_load")}</AlertTitle>
                  {error.message}
                </Alert>
              </Paper>
            )}
            {!isLoading && (
              <Stack
                alignItems="center"
                display={"flex"}
                sx={{
                  flexDirection: { xs: "column", md: "row" },
                  rowGap: 1,
                  m: 1,
                  width: "100%",
                }}
              >
                <Pagination
                  color="primary"
                  shape="rounded"
                  count={
                    isLocalSearch
                      ? Math.ceil((dataSearch.length ?? 0) / localItemPerPage)
                      : pageCount
                  }
                  page={isLocalSearch ? localPageSearch : page}
                  onChange={changePage}
                  siblingCount={0}
                  boundaryCount={1}
                ></Pagination>
                <FormControl
                  sx={{
                    m: 1,
                    minWidth: { xs: "auto", md: 120 },
                    display: "inline-block",
                  }}
                  size="small"
                >
                  <Select
                    sx={{
                      float: "left",
                      backgroundColor: "#fff",
                      "> .MuiInputBase-root": {
                        borderRadius: "2px !important",
                      },
                    }}
                    id="ItemPerPage"
                    value={
                      isLocalSearch ? `${localItemPerPage}` : `${itemPerPage}`
                    }
                    onChange={handlePagesizeChange}
                  >
                    {PAGE_SIZE_OPTIONS.map((size, index) => (
                      <MenuItem
                        key={`ItemPerPage${index}`}
                        color="#1A1A1A"
                        value={size}
                      >
                        {size}/{t("src_pagination_page")}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography
                    sx={{
                      float: "left",
                      height: "40px",
                      paddingTop: "10px",
                      marginLeft: 1,
                    }}
                    color="#1A1A1A"
                    component={"div"}
                  >
                    {t("src_pagination_goto")}
                  </Typography>
                  <TextField
                    sx={{
                      backgroundColor: "#fff",
                      width: "60px",
                      marginLeft: 1,
                      "> .MuiInputBase-root": { borderRadius: "2px" },
                      color: "#1A1A1A",
                    }}
                    id="GotoPage"
                    variant="outlined"
                    onChange={handlePageChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    size="small"
                  />
                </FormControl>
              </Stack>
            )}
          </Stack>
        </Grid>
        {isLogin && !isDemo && (
          <Grid
            size={12}
            sx={{
              backgroundColor: "#fff",
              textAlign: "center",
              justifyItems: "center",
              height: "144px",
              padding: "30px 0px",
              marginTop: "21px",
            }}
          >
            <ContactRequestButton
              t={t}
              products={coloredMaterials?.results}
              selectedMaterial={selectedMaterial}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
